import { useTraduccion, type Clave } from '@tallerya/i18n';
import {
  Boton,
  CampoDocumento,
  CampoSeleccion,
  CampoTexto,
  Fila,
  Formulario,
  Seccion,
  useAvisos,
  useFormulario,
  type EstadoFormulario,
} from '@tallerya/ui';
import { useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { EliminarProveedor } from './EliminarProveedor';
import {
  pedirSiguienteCodigo,
  useCambiosProveedor,
  useFichaProveedor,
  useSiguienteCodigo,
  useTiposDocumento,
  version,
  type FichaProveedor,
  type ProblemaProveedor,
  type ValoresProveedor,
} from './datos';

// Alta y edición de un proveedor (#56): «Proveedores - Nuevo proveedor»,
// «(Errores)» y «Editar proveedor» del Figma. La misma pantalla para las dos,
// como en sucursales:
//
//   /proveedores/nuevo        alta: el código llega ya propuesto
//   /proveedores/:id/editar   edición: se pide la ficha, con la versión para
//                             guardar; `/proveedores/:id` es la ficha (#188)
//
// Solo el nombre es obligatorio: el proveedor muchas veces se da de alta con
// prisa, y el resto se completa después.

/** El alta en blanco. El código lo pone la propuesta de `next-code`. */
const ALTA: ValoresProveedor = { code: '', name: '', docTypeId: '', docIdentifier: '', reference: '' };

/** La frase del aviso según qué chocó, cuando la API dice con quién. */
const REPETIDO: Record<string, Clave> = {
  'supplier.nombre_duplicado': 'admin.proveedores.error.repetidoNombre',
  'supplier.documento_tomado': 'admin.proveedores.error.repetidoDocumento',
  'supplier.nombre_y_documento_duplicados': 'admin.proveedores.error.repetidoAmbos',
};

export function FormularioProveedor() {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const { id } = useParams();
  const alta = id === undefined;
  const ficha = useFichaProveedor(id);
  const siguiente = useSiguienteCodigo(alta);
  useTituloPantalla(alta ? t('admin.proveedores.nuevo') : t('admin.proveedores.editar.titulo'));

  if (alta) {
    // Se espera la propuesta antes de dibujar, para que el formulario arranque con
    // ella. Si falla, el alta sigue con el código vacío: no se bloquea por eso.
    if (siguiente.isPending) return <p className="entrada">{t('app.cargando')}</p>;

    return <Editor key="nuevo" codigoPropuesto={siguiente.data ?? ''} />;
  }

  if (ficha.isPending) return <p className="entrada">{t('app.cargando')}</p>;

  if (ficha.isError || ficha.data === null) {
    const noExiste = ficha.data === null;
    return (
      <section className="aviso">
        <h2>{noExiste ? t('admin.proveedores.noExiste.titulo') : t('ui.listado.error.titulo')}</h2>
        <p>{noExiste ? t('admin.proveedores.noExiste.texto') : t('ui.listado.error.texto')}</p>
        <Boton variante="secundario" onClick={() => void navegar('/proveedores')}>
          {t('admin.proveedores.volver')}
        </Boton>
      </section>
    );
  }

  // La clave lleva la versión: si la ficha cambia en la base y se vuelve a pedir,
  // el formulario arranca de nuevo con los valores frescos.
  return <Editor key={`${ficha.data.id}-${ficha.data.version}`} ficha={ficha.data} />;
}

interface PropsEditor {
  ficha?: FichaProveedor;
  /** Solo el alta: el código que tendría el próximo proveedor, o vacío si no se pudo saber. */
  codigoPropuesto?: string;
}

function Editor({ ficha, codigoPropuesto = '' }: PropsEditor) {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const { avisar } = useAvisos();
  const cambios = useCambiosProveedor();
  const tiposDoc = useTiposDocumento();
  const [eliminando, setEliminando] = useState(false);
  // El código que se mostró ya puesto. Si al guardar sigue igual, nadie lo tocó.
  const [propuesto, setPropuesto] = useState(codigoPropuesto);
  const editando = ficha !== undefined;
  // Con algo detrás —la ficha o el listado con sus filtros— se vuelve con
  // «Atrás». Quien entró por un enlace va al listado limpio.
  const origen = useLocation().state as { desdeFicha?: boolean; desdeListado?: boolean } | null;
  const salir = () => void (origen?.desdeFicha || origen?.desdeListado ? navegar(-1) : navegar('/proveedores'));
  // Eliminado, su ficha ya no existe: se salta hasta lo que había detrás de ella
  // —`desdeListado` lo dice—, o se reemplaza esta pantalla por el listado limpio.
  const alEliminar = () =>
    void (origen?.desdeFicha
      ? origen.desdeListado
        ? navegar(-2)
        : navegar('/proveedores', { replace: true })
      : salir());

  const form = useFormulario<ValoresProveedor>({
    inicial: ficha
      ? {
          code: ficha.code,
          name: ficha.name,
          docTypeId: ficha.docTypeId ? String(ficha.docTypeId) : '',
          docIdentifier: ficha.docIdentifier ?? '',
          reference: ficha.reference ?? '',
        }
      : { ...ALTA, code: codigoPropuesto },
    // La cortesía de no ir y volver por lo obvio. La que vale es la de la API.
    validar: (v) => ({
      ...(editando && !v.code.trim() && { code: 'validation.required' }),
      // El documento va en pareja: es la misma regla del validador de la API.
      ...(v.docTypeId && !v.docIdentifier.trim() && { docIdentifier: 'validation.required' }),
      ...(!v.docTypeId && v.docIdentifier.trim() && { docTypeId: 'validation.required' }),
    }),
    guardar: async (v) =>
      ficha
        ? cambios.editar(String(ficha.id), version(ficha.version), ficha.isActive, v)
        : (await cambios.crear(v, propuesto)).problema,
    // Los choques que tienen un campo: se marcan bajo él y no arriba. El de nombre
    // y documento juntos pinta los dos de una vez, cada uno con su mensaje.
    camposPorCodigo: {
      'supplier.codigo_tomado': 'code',
      'supplier.nombre_duplicado': 'name',
      'supplier.documento_tomado': 'docIdentifier',
      'supplier.nombre_y_documento_duplicados': {
        name: 'supplier.nombre_duplicado',
        docIdentifier: 'supplier.documento_tomado',
      },
    },
    // Quién ya los tiene, y el camino a él: «Repuestos del Sur (PRV-0001) ya está
    // registrado con ese nombre y ese documento. Ver proveedor existente →». Si el
    // nombre es de uno y el documento de otro, la API no nombra a nadie.
    completarAviso: (problema: ProblemaProveedor) => {
      const existente = problema.existingSupplier;
      const clave = problema.code ? REPETIDO[problema.code] : undefined;
      if (!existente || !clave) return undefined;
      return {
        texto: t(clave, { nombre: existente.name, codigo: existente.code }),
        enlace: {
          texto: t('admin.proveedores.error.verExistente'),
          // Abre su ficha (#188), y reemplaza al formulario en el historial:
          // «Atrás» desde el existente no vuelve a un alta que ya no tiene sentido.
          alHacerClic: () => void navegar(`/proveedores/${String(existente.id)}`, { replace: true }),
        },
      };
    },
  });

  // Si la persona escribe su código mientras llega el propuesto de «Guardar y
  // agregar otro», manda el suyo. Es una ref y no estado porque solo se lee en la
  // respuesta, no al dibujar. Como en `FormularioItem`.
  const codigoTocado = useRef(false);
  const formCodigo: EstadoFormulario<ValoresProveedor> = {
    ...form,
    cambiar: (campo, valor) => {
      if (campo === 'code') codigoTocado.current = true;
      form.cambiar(campo, valor);
    },
  };

  const opcionesDoc = (tiposDoc.data ?? []).map((td) => ({ valor: String(td.id), etiqueta: td.code }));
  // El tipo que ya tenía el proveedor aunque el taller lo haya apagado: sin esto,
  // el combo lo mostraría vacío. Si ya no aplica, al guardar lo dice la API.
  if (ficha?.docTypeId && ficha.docTypeCode && !opcionesDoc.some((o) => o.valor === String(ficha.docTypeId))) {
    opcionesDoc.push({ valor: String(ficha.docTypeId), etiqueta: ficha.docTypeCode });
  }
  // La forma del número del tipo elegido (#155). Un tipo que el taller apagó no
  // viene en la lista y queda sin máscara: se escribe libre.
  const mascaraDoc = tiposDoc.data?.find((td) => String(td.id) === form.valores.docTypeId)?.mask;

  async function guardado(siguiente: 'volver' | 'otro') {
    const nombre = form.valores.name.trim();
    avisar({
      titulo: editando
        ? t('admin.proveedores.aviso.guardado', { nombre })
        : t('admin.proveedores.aviso.creado', { nombre }),
    });
    if (siguiente === 'otro') {
      // Se limpia antes de pedir el código: con los datos del recién creado a la
      // vista, un segundo «Guardar» en la espera daría de alta un duplicado.
      codigoTocado.current = false;
      form.reiniciar(ALTA);
      setPropuesto('');
      requestAnimationFrame(() => document.getElementById(form.idDe('name'))?.focus());
      const codigo = await pedirSiguienteCodigo();
      if (codigoTocado.current) return;
      setPropuesto(codigo);
      form.cambiar('code', codigo);
      return;
    }
    salir();
  }

  const estado = ficha?.isActive ? t('admin.proveedores.activo') : t('admin.proveedores.inactivo');

  return (
    <>
      <Formulario
        form={form}
        titulo={editando ? t('admin.proveedores.editar.titulo') : t('admin.proveedores.nuevo')}
        info={editando ? t('admin.proveedores.editar.info') : t('admin.proveedores.nuevo.info')}
        // Al editar, de qué proveedor se trata: «PRV-0002 · Lubricantes Martínez · Activo».
        {...(ficha && { descripcion: `${ficha.code} · ${ficha.name} · ${estado}` })}
        {...(editando && { etiquetaGuardar: t('admin.proveedores.formulario.guardarCambios') })}
        guardarYOtro={!editando}
        onGuardado={(siguiente) => void guardado(siguiente)}
        onCancelar={salir}
        {...(ficha && {
          accionSecundaria: (
            <Boton variante="peligro" onClick={() => setEliminando(true)}>
              {t('admin.proveedores.formulario.eliminar')}
            </Boton>
          ),
        })}
      >
        <Seccion titulo={t('admin.proveedores.formulario.seccion')} info={t('admin.proveedores.formulario.seccionInfo')}>
          <Fila>
            <CampoTexto
              form={formCodigo}
              nombre="code"
              etiqueta={t('admin.proveedores.formulario.codigo')}
              // En el alta, vacío vale: la API asigna el código.
              obligatorio={editando}
              {...(!editando && { info: t('admin.proveedores.formulario.codigoAyuda') })}
              ancho="corto"
              autoComplete="off"
            />
            <CampoTexto
              form={form}
              nombre="name"
              etiqueta={t('admin.proveedores.formulario.nombre')}
              obligatorio
              placeholder={t('admin.proveedores.formulario.nombreEjemplo')}
              autoComplete="off"
            />
          </Fila>
          <Fila>
            <CampoSeleccion
              form={form}
              nombre="docTypeId"
              etiqueta={t('admin.proveedores.formulario.tipoDocumento')}
              placeholder={t('admin.proveedores.formulario.sinDocumento')}
              opciones={opcionesDoc}
            />
            <CampoDocumento
              form={form}
              nombre="docIdentifier"
              etiqueta={t('admin.proveedores.formulario.numeroDocumento')}
              ancho="corto"
              mascara={mascaraDoc}
            />
            <CampoTexto
              form={form}
              nombre="reference"
              etiqueta={t('admin.proveedores.formulario.referencia')}
              info={t('admin.proveedores.formulario.referenciaAyuda')}
              placeholder={t('admin.proveedores.formulario.referenciaEjemplo')}
              ancho="corto"
              autoComplete="off"
            />
          </Fila>
        </Seccion>
      </Formulario>

      <EliminarProveedor
        proveedor={eliminando ? (ficha ?? null) : null}
        onCerrar={() => setEliminando(false)}
        onEliminado={alEliminar}
      />
    </>
  );
}
